/* FindImagesAction.js */

"use strict";

const FindImagesSummaryComponent = require("./veils/summary/FindImagesSummaryComponent");

/**
 * Action for finding/resolving upgrade links (images) for IoT Platforms.
 * Launches the 'getOsUpgradeLinks' work for all platforms in the organization.
 *
 * Action Availability:
 * - Always enabled (no platform selection required)
 *
 * Workflow:
 * 1. Action launches getOsUpgradeLinks work for all platforms
 * 2. Displays progress veil with Hide button
 * 3. On completion, shows summary with success/failure counts
 * 4. Results viewable in platform activity views
 */
class FindImagesAction {
    constructor(logger, gameEditor, modalUserFeedback, OdmOsPlatform, userSession) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._OdmOsPlatform = OdmOsPlatform;
        this._userSession = userSession;

        this.run = this.run.bind(this);
    }

    /**
     * Executes the Find Images action.
     * Launches getOsUpgradeLinks work for all platforms in the organization.
     *
     * @returns {Promise} Promise that resolves when action completes
     */
    run() {
        this._logger.info("FindImagesAction - Starting getOsUpgradeLinks work for all platforms");

        return this._runGetOsUpgradeLinksWork();
    }

    /**
     * Runs the getOsUpgradeLinks work for all platforms in the organization
     * @returns {Promise}
     * @private
     */
    async _runGetOsUpgradeLinksWork() {
        try {
            // Get total platform count from the change feed
            const activeView = this._gameEditor._activeView;
            const totalPlatforms =
                activeView && activeView.pagingFeed
                    ? activeView.pagingFeed.totalEntities
                    : undefined;

            // Show progress veil
            this._modalUserFeedback.displayLoading({
                message: "Finding images...",
                showHideButton: true,
            });

            // Define work target - run on all platforms in organization
            const workTarget = {
                category: "entity",
                entityType: "OdmOsPlatform",
            };

            // Define work input (empty object for getOsUpgradeLinks)
            const workInput = {};

            // Progress callback to update the veil
            const progressCallback = (progress) => {
                const successCount = progress.successCount || 0;
                const failureCount = progress.failureCount || 0;
                const completed = successCount + failureCount;
                const total = totalPlatforms || completed;
                const plural = total === 1 ? "IoT Platform" : "IoT Platforms";
                const count = totalPlatforms ? `${completed} of ${totalPlatforms}` : `${completed}`;
                this._modalUserFeedback.showLoadingProgress(
                    `Image search completed for ${count} ${plural}`
                );
            };

            // Run the work
            const result = await this._OdmOsPlatform.runGetOsUpgradeLinksWork(
                this._userSession.activeOrganization.id,
                workTarget,
                workInput,
                {launchAndForget: false},
                progressCallback
            );

            this._logger.info("FindImagesAction - Work completed");

            // Hide progress veil
            await this._modalUserFeedback.hideDisplayed();

            // Check if user closed/aborted the progress veil
            if (result.closed || result.aborted) {
                return Promise.resolve();
            }

            // Show summary veil with results
            const summaryData = this._processSummaryData(result, totalPlatforms);
            const summaryComponent = new FindImagesSummaryComponent(summaryData, this._logger);

            await this._modalUserFeedback.displayCustom(summaryComponent);

            // Refresh grid
            this._refreshGrid();

            return Promise.resolve();
        } catch (err) {
            // Handle user cancellation or errors
            this._modalUserFeedback.hideDisplayed();

            if (err.userRejected || err.closed || err.aborted) {
                this._logger.warn("FindImagesAction.run() - User canceled");
            } else {
                this._logger.error({error: err}, "FindImagesAction.run() - Error");
                await this._modalUserFeedback.displayError(err);
            }

            return Promise.reject(err);
        }
    }

    /**
     * Processes work result into summary data format
     * @param {Object} result - Work result object
     * @returns {Object} Summary data
     * @private
     */
    _processSummaryData(result, totalPlatforms) {
        // Extract success/failure counts from work result summary
        const summary = result.summary || result;
        const successCount = summary.successCount || 0;
        const failureCount = summary.failureCount || 0;
        const total = totalPlatforms || successCount + failureCount;
        const skippedCount = total - successCount - failureCount;
        const workId = summary.workId || result.id;

        // Process targetErrors object into array format for display
        const targetErrors = result.targetErrors || {};
        const platformNames = result.targetErrorNames || {};
        const errors = Object.entries(targetErrors).map(([platformId, errorMsg]) => ({
            platformName: platformNames[platformId] || platformId,
            platformId,
            message: errorMsg,
        }));

        return {
            total,
            successCount,
            failureCount,
            skippedCount,
            errors,
            workId,
        };
    }

    /**
     * Refreshes the platform grid to update any changed data
     * @private
     */
    _refreshGrid() {
        const activeView = this._gameEditor._activeView;
        if (activeView && activeView.pagingFeed && activeView.pagingFeed.feedUpdate) {
            activeView.pagingFeed.feedUpdate();
        }
    }
}

module.exports = FindImagesAction;

FindImagesAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.OdmOsPlatform",
    "$teve.userSession",
];

FindImagesAction.$displayDetails = {
    type: "Action",
    name: "FindImagesAction",
    icon: "osmanager.find_images",
    shown: true,
    title: "Find Images",
    order: 4,
};
